import itemSchema from '@/store/modules/schemas/user.schema'
import ModuleBase from '@/store/modules/ModuleBase'
import axios from '@/main/axios'
import {isInteger} from 'lodash'
import requestManager from '@/utils/requestManager'

const resourceName = 'users'
const base = ModuleBase(itemSchema, resourceName)
export const ReviewStatus = {
  NEW: 0,
  PROBLEM: 1,
  REVIEW: 2,
  GOOD: 3,
  PERFECT: 4
}
export default {
  namespaced: base.namespaced,
  state: {
    ...base.state,
    authenticatedUser: null,
    reviewStatus: {
      0: 'new',
      1: 'problem',
      2: 'review',
      3: 'good',
      4: 'perfect'
    },
    countUserNeedReview: 0,
    currentUserDetails: null,
    shouldRefreshReviewTable: false,
    prefetchingOffersOverview: {}, // { userId: true/false }
    prefetchedOffersOverview: {}, // { userId: { data: [], timestamp: number } }
    prefetchPromises: {} // { userId: Promise }
  },
  getters: {
    ...base.getters,
    authenticatedUser: state => state.authenticatedUser,
    getByHash: state => userHash => {
      const user = state.all.filter(u => u.hash === userHash)
      return user.length ? user[0] : {}
    },
    getReviewStatusText: state => statusId => {
      if (!isInteger(statusId)) statusId = parseInt(statusId)
      return state.reviewStatus[statusId]
    },
    countUserNeedReview: state => state.countUserNeedReview,
    getCurrentUserDetails: state => state.currentUserDetails,
    isPrefetchingOffersOverview: state => userId => state.prefetchingOffersOverview[userId] === true,
    getPrefetchedOffersOverview: state => userId => {
      const prefetched = state.prefetchedOffersOverview[userId]
      if (!prefetched) return null
      // Check if data is less than 5 minutes old
      const maxAge = 5 * 60 * 1000 // 5 minutes
      if (prefetched.timestamp && (Date.now() - prefetched.timestamp) < maxAge) {
        return prefetched.data
      }
      // Data is expired, return null (cleanup will happen in mutation)
      return null
    },
    getPrefetchPromise: state => userId => state.prefetchPromises[userId] || null
  },
  mutations: {
    ...base.mutations,
    SET_AUTHENTICATED_USER (state, payload) {
      state.authenticatedUser = payload
    },
    SET_COUNT_USER_NEED_REVIEW (state, count) {
      state.countUserNeedReview = count
    },
    SET_CURRENT_USER_DETAIL (state, currentUserDetails) {
      state.currentUserDetails = currentUserDetails
    },
    TOGGLE_REFRESH_REVIEWS_TABLE (state) {
      state.shouldRefreshReviewTable = !state.shouldRefreshReviewTable
    },
    SET_PREFETCHING_OFFERS_OVERVIEW (state, { userId, isPrefetching }) {
      if (isPrefetching) {
        state.prefetchingOffersOverview[userId] = true
      } else {
        delete state.prefetchingOffersOverview[userId]
      }
    },
    SET_PREFETCHED_OFFERS_OVERVIEW (state, { userId, data }) {
      // First, clean up expired data (older than 5 minutes)
      const maxAge = 5 * 60 * 1000 // 5 minutes
      const now = Date.now()
      const userIds = Object.keys(state.prefetchedOffersOverview)
      
      userIds.forEach(id => {
        const prefetched = state.prefetchedOffersOverview[id]
        if (prefetched && prefetched.timestamp && (now - prefetched.timestamp) >= maxAge) {
          // Remove expired data
          delete state.prefetchedOffersOverview[id]
          delete state.prefetchPromises[id]
          delete state.prefetchingOffersOverview[id]
        }
      })
      
      // Limit to 10 most recent users to prevent memory issues
      const maxUsers = 10
      const remainingUserIds = Object.keys(state.prefetchedOffersOverview)
      
      // If we're at the limit and adding a new user, remove the oldest one
      if (remainingUserIds.length >= maxUsers && !state.prefetchedOffersOverview[userId]) {
        // Find the oldest entry by timestamp
        let oldestUserId = remainingUserIds[0]
        let oldestTimestamp = state.prefetchedOffersOverview[oldestUserId].timestamp
        
        remainingUserIds.forEach(id => {
          const timestamp = state.prefetchedOffersOverview[id].timestamp
          if (timestamp < oldestTimestamp) {
            oldestTimestamp = timestamp
            oldestUserId = id
          }
        })
        
        // Remove the oldest entry
        delete state.prefetchedOffersOverview[oldestUserId]
        delete state.prefetchPromises[oldestUserId]
        delete state.prefetchingOffersOverview[oldestUserId]
      }
      
      // Add/update the new user's data
      state.prefetchedOffersOverview[userId] = {
        data,
        timestamp: Date.now()
      }
    },
    REMOVE_PREFETCHED_OFFERS_OVERVIEW (state, userId) {
      delete state.prefetchedOffersOverview[userId]
      // Also clean up related data
      delete state.prefetchPromises[userId]
      delete state.prefetchingOffersOverview[userId]
    },
    CLEANUP_EXPIRED_PREFETCH_DATA (state) {
      // Clean up all expired data (older than 5 minutes)
      const maxAge = 5 * 60 * 1000 // 5 minutes
      const now = Date.now()
      const userIds = Object.keys(state.prefetchedOffersOverview)
      
      userIds.forEach(id => {
        const prefetched = state.prefetchedOffersOverview[id]
        if (prefetched && prefetched.timestamp && (now - prefetched.timestamp) >= maxAge) {
          // Remove expired data
          delete state.prefetchedOffersOverview[id]
          delete state.prefetchPromises[id]
          delete state.prefetchingOffersOverview[id]
        }
      })
    },
    SET_PREFETCH_PROMISE (state, { userId, promise }) {
      if (promise) {
        state.prefetchPromises[userId] = promise
      } else {
        delete state.prefetchPromises[userId]
      }
    },
    CLEAR_USER_PREFETCH_DATA (state, userId) {
      // Clear all prefetch data for a specific user
      delete state.prefetchingOffersOverview[userId]
      delete state.prefetchedOffersOverview[userId]
      delete state.prefetchPromises[userId]
    },
    CLEAR_ALL_PREFETCH_DATA (state) {
      // Clear all prefetch data (useful when navigating away)
      state.prefetchingOffersOverview = {}
      state.prefetchedOffersOverview = {}
      state.prefetchPromises = {}
    }
  },
  actions: {
    ...base.actions,
    setAuthenticatedUser: async ({commit}, payload) => {
      return commit('SET_AUTHENTICATED_USER', payload)
    },
    getStatisticsData: async ({commit}, userId) => {
      return await axios.get(`${base.basePath + resourceName  }/get-statistics-data/${ userId }`)
    },
    getCustomStatisticsData: async ({commit}, {userId, statisticRelations}) => {
      return await axios.get(`${base.basePath + resourceName  }/get-statistics-data/${ userId }`,
        {params: {statisticRelations}}
      )
    },
    updateReviewStatus: async ({commit}, userData) => {
      return await axios.post(`${base.basePath + resourceName  }/update-review-status`, userData)
    },
    getListUsersToReview: async () => {
      return await axios.post(`${base.basePath + resourceName  }/get-list-user-to-review`)
    },
    countUserNeedReview: async ({commit}) => {
      const requestKey = 'user_count_need_review'
      
      return await requestManager.execute(requestKey, async () => {
        const res = await axios.post(`${base.basePath + resourceName  }/count-user-need-review`)
        const count = res.data ? res.data.data : 0
        commit('SET_COUNT_USER_NEED_REVIEW', count)
        return res.data
      })
    },
    getUsersToReview: async () => {
      return await axios.post(`${base.basePath + resourceName  }/get-user-to-review`)
    },
    getByHash: async ({commit}, requestData) => {
      const res = await axios.get(`${base.basePath + resourceName  }/get-by-hash/${  requestData.hash}`, {params: requestData.params})
      const item = res.status === 200 && res.data ? res.data : null
      if (!item) return null
      //find item in list state and update it
      await commit('UPSERT', item)
      return item
    }
  }
}
